import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import toast, { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute } from './hooks/useProtectedRoute';
import { useBackendHealth } from './hooks/useBackendHealth';
import BackendErrorBoundary from './components/atoms/BackendErrorBoundary';
import { setToastErrorHandler } from './services/api';
import MainLayout from './components/organisms/MainLayout';
import Dashboard from './pages/dashboard/Dashboard';
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';
import Onboarding from './pages/auth/Onboarding';
import ClinicSetup from './pages/auth/ClinicSetup';
import PetOwnerSetup from './pages/auth/PetOwnerSetup';
import Patients from './pages/patients/Patients';
import Appointments from './pages/appointments/Appointments';
import MedicalRecords from './pages/medical-records/MedicalRecords';
import Invoices from './pages/invoices/Invoices';
import Inventory from './pages/inventory/Inventory';
import Users from './pages/users/Users';
import AIChatFullPage from './pages/ai-chat/AIChatFullPage';
import MaintenancePage from './pages/maintenance/MaintenancePage';


// Health check wrapper component
const AppWithHealthCheck: React.FC = () => {
  const { isHealthy, isLoading, lastChecked } = useBackendHealth();

  useEffect(() => {
    // Register toast error handler for API errors
    setToastErrorHandler((message, type = 'error') => {
      switch (type) {
        case 'error':
          toast.error(message);
          break;
        case 'warning':
          toast(message, { icon: '⚠️' });
          break;
        case 'info':
          toast(message, { icon: 'ℹ️' });
          break;
      }
    });
  }, []);

  // Show maintenance page if backend is not healthy (and not the initial loading state)
  if (!isHealthy && !isLoading && lastChecked) {
    return <MaintenancePage />;
  }

  return <AppContent />;
};

// Inner component to access toast context
const AppContent: React.FC = () => {
  return (
    <Router>
      <div className="min-h-screen bg-canvas text-slate-900">
        {/* react-hot-toast */}
        <Toaster
          position="top-right"
          reverseOrder={false}
          gutter={8}
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
            success: {
              duration: 3000,
              iconTheme: {
                primary: '#4CAF50',
                secondary: '#fff',
              },
            },
            error: {
              duration: 5000,
              iconTheme: {
                primary: '#f44336',
                secondary: '#fff',
              },
            },
          }}
        />
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/onboarding" element={
            <ProtectedRoute>
              <Onboarding />
            </ProtectedRoute>
          } />
          <Route path="/clinic-setup" element={
            <ProtectedRoute>
              <ClinicSetup />
            </ProtectedRoute>
          } />
          <Route path="/pet-owner-setup" element={
            <ProtectedRoute>
              <PetOwnerSetup />
            </ProtectedRoute>
          } />

          {/* Protected routes with main layout */}
          <Route path="/" element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="patients" element={<Patients />} />
            <Route path="appointments" element={<Appointments />} />
            <Route path="medical-records" element={<MedicalRecords />} />
            <Route path="invoices" element={<Invoices />} />
            <Route path="inventory" element={<Inventory />} />
            <Route path="users" element={<Users />} />
          </Route>

          {/* Standalone AI Chat (full page) */}
          <Route path="/ai-chat-full" element={
            <ProtectedRoute>
              <AIChatFullPage />
            </ProtectedRoute>
          } />

          {/* Fallback route */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </div>
    </Router>
  );
};

function App() {
  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';

  return (
    <div data-theme="vetpintar" className="min-h-screen bg-base-100 text-base-content">
      <GoogleOAuthProvider clientId={googleClientId}>
        <AuthProvider>
          <BackendErrorBoundary>
            <AppWithHealthCheck />
          </BackendErrorBoundary>
        </AuthProvider>
      </GoogleOAuthProvider>
    </div>
  );
}

export default App;
